/// <amd-module name="Wit/Common/BacklogContentService/RollupHelpers" />
import { format } from "VSS/Core/Util/String";
import * as Resources from "Wit/Common/BacklogContentService/Resources";

// Temporary hosting enums here to avoid taking dependency on Analytics code outside of Backlogs grid
// TODO: remove when completing #1610969

// Taken mainly from Tfs/Service/WebAccess/WorkItemTracking/Scripts/Utils/RollupHelpers.ts

export enum RollupType {
    Progress,
    Total
}

export enum RollupAggregation {
    Count = 1,
    Sum = 2
}
export enum BacklogFieldTypes {
    Effort = 1,
    Order = 2,
    RemainingWork = 3,
    Team = 4,
    Activity = 5,
    Requestor = 6,
    ApplicationType = 7,
    ApplicationStartInformation = 8,
    ApplicationLaunchInstructions = 9,
    FeedbackNotes = 10,
    ClosedDate = 11
}

export interface RollupCalculationSettings {
    workItemTypeFilter?: string;
    backlogFilter?: string;
    type: RollupType;
    aggregation: RollupAggregation;
    aggregationField?: string;
    aggregationFieldDisplayName?: string;
}
export interface IRollupColumn extends INotAFieldColumn {
    rollupCalculation: RollupCalculationSettings;
}

export interface INotAFieldColumn {
    referenceName: string;
    displayName: string;
}

export function getRollupColumnReferenceName(rollupMetric: RollupCalculationSettings, isCustom?: boolean): string {
    let referenceName = "System.Backlog.Rollup.";
    if (isCustom) {
        referenceName += "Custom.";
    }
    switch (rollupMetric.type) {
        case RollupType.Progress:
            referenceName += "ProgressBy.";
            break;
        case RollupType.Total:
            referenceName += "Total.";
    }

    if (rollupMetric.aggregation === RollupAggregation.Sum) {
        if (rollupMetric.aggregationField) {
            referenceName += rollupMetric.aggregationField + ".";
        }
    }

    if (rollupMetric.workItemTypeFilter) {
        referenceName += rollupMetric.workItemTypeFilter.replace(/\s/g, "");
    } else if (rollupMetric.backlogFilter) {
        referenceName += rollupMetric.backlogFilter.replace(/\s/g, "");
    } else {
        referenceName += "AllCompletedDescendants";
    }

    return referenceName;
}

export function getRollupColumnDisplayName(rollupMetric: RollupCalculationSettings, friendlyNames?: { backlogName?: string; fieldName?: string }) {
    if (!friendlyNames) {
        friendlyNames = {
            backlogName: rollupMetric.backlogFilter,
            fieldName: rollupMetric.aggregationFieldDisplayName ?? rollupMetric.aggregationField
        };
    }

    if (rollupMetric.type === RollupType.Progress) {
        if (rollupMetric.workItemTypeFilter) {
            if (rollupMetric.aggregation === RollupAggregation.Sum) {
                return format(Resources.ProgressByFilterField, rollupMetric.workItemTypeFilter, friendlyNames && friendlyNames.fieldName);
            }
            return format(Resources.ProgressByValue, rollupMetric.workItemTypeFilter);
        }

        if (rollupMetric.backlogFilter) {
            if (rollupMetric.aggregation === RollupAggregation.Sum) {
                return format(Resources.ProgressByFilterField, friendlyNames && friendlyNames.backlogName, friendlyNames && friendlyNames.fieldName);
            }
            return format(Resources.ProgressByValue, friendlyNames && friendlyNames.backlogName);
        }

        if (rollupMetric.aggregation === RollupAggregation.Sum) {
            return format(Resources.ProgressByValue, friendlyNames && friendlyNames.fieldName);
        }

        return Resources.ProgressByCompletedDescendents;
    }

    if (rollupMetric.workItemTypeFilter) {
        if (rollupMetric.aggregation === RollupAggregation.Sum) {
            return format(Resources.TotalByFilterField, rollupMetric.workItemTypeFilter, friendlyNames && friendlyNames.fieldName);
        }
        return format(Resources.TotalByValue, rollupMetric.workItemTypeFilter);
    }

    if (rollupMetric.backlogFilter) {
        if (rollupMetric.aggregation === RollupAggregation.Sum) {
            return format(Resources.TotalByFilterField, friendlyNames && friendlyNames.backlogName, friendlyNames && friendlyNames.fieldName);
        }
        return format(Resources.TotalByValue, friendlyNames && friendlyNames.backlogName);
    }

    if (rollupMetric.aggregation === RollupAggregation.Sum) {
        return format(Resources.TotalByFieldName, friendlyNames && friendlyNames.fieldName);
    }

    return Resources.TotalByAllDescendents;
}

export function isRollupEqual(a: RollupCalculationSettings, b: RollupCalculationSettings): boolean {
    return (
        a.aggregation === b.aggregation &&
        a.aggregationField === b.aggregationField &&
        a.backlogFilter === b.backlogFilter &&
        a.type == b.type &&
        a.workItemTypeFilter == b.workItemTypeFilter
    );
}
